import { useCallback, useEffect, useState } from 'react';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { LOAD_SUBTASKS_EXPERIENCE } from '../../common/constants';
import type { BoardIssueOld } from '../../common/types';
import { useBoardStore, useGetIssueById } from '../../services/board-store';
import { useGetSubtasks } from '../../services/get-subtasks-jis';
import LocalStorage from '../../utils/local-storage-old';
import { useSelectedFields } from '../fields-preference';
import { batchIssuesForSubtasks } from './utils';

/** @deprecated remove when jwm.board-awesome-enhancements is cleaned up */
export const useBoardSubtasks = () => {
	const [{ issues }, { updateIssue }] = useBoardStore();
	const [getIssueById] = useGetIssueById();
	const { getSubtasks } = useGetSubtasks();
	const projectData = useProject();
	const projectId = String(projectData.id);
	const startExperience = useExperienceStart(LOAD_SUBTASKS_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(LOAD_SUBTASKS_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(LOAD_SUBTASKS_EXPERIENCE);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const selectedFields = useSelectedFields();
	const [initialSubtasksLoaded, setInitialSubtasksLoaded] = useState(false);
	const [optimisticUpdate, setOptimisticUpdate] = useState<number[]>([]);
	const [errorIssueIds, setErrorIssueIds] = useState<number[]>([]);

	const batchLoadSubtasks = useCallback(
		async (parentIssueIds: number[]) => {
			const { data: oldSubtasks, error } = await getSubtasks({ parentIssueIds });

			if (oldSubtasks != null) {
				const issuesToUpdate: {
					[key: number]: BoardIssueOld[];
				} = {};

				oldSubtasks.forEach((subtask) => {
					if (subtask.parentId != null) {
						const parentIssueId = subtask.parentId;
						if (!issuesToUpdate[parentIssueId]) {
							issuesToUpdate[parentIssueId] = [];
						}
						issuesToUpdate[parentIssueId].push(subtask);
					}
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				Object.entries(issuesToUpdate).forEach(([issueId, issueSubtasks]: [any, any]) => {
					updateIssue(+issueId, { subtasks: issueSubtasks });
				});
				return true;
			}
			if (error != null) {
				setErrorIssueIds(parentIssueIds);
			}
			return false;
		},
		[getSubtasks, updateIssue],
	);

	// initial load of expanded subtasks from local storage
	useEffect(() => {
		if (!initialSubtasksLoaded && issues.length > 0 && selectedFields.subtasks) {
			setInitialSubtasksLoaded(true);
			startExperience();
			const expandedIssueIds = LocalStorage.getExpandedTasks(projectId);
			const expandedIssueIdsWithSubtasks = expandedIssueIds.filter(
				(issueId) => getIssueById(issueId)?.subtaskMetadata?.total > 0,
			);

			// optimistic update to show loading skeleton
			expandedIssueIdsWithSubtasks.forEach((issueId) => {
				updateIssue(+issueId, { subtasks: [] });
			});
			setOptimisticUpdate([...expandedIssueIdsWithSubtasks]);

			// we batch so that we get < 100 issues in a response and avoid need for pagination
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const subtaskRequests: Array<any> = [];
			const batches = batchIssuesForSubtasks(issues, expandedIssueIdsWithSubtasks);
			batches.forEach((parentIssueIds) => {
				subtaskRequests.push(batchLoadSubtasks(parentIssueIds));
			});

			Promise.all(subtaskRequests).then((values) => {
				if (values.filter((v) => v).length === values.length) {
					// clean up any old issue ids out of local storage eg. for done tasks no longer on the board
					const cleanedIssueIds = expandedIssueIds.filter(
						(issueId) =>
							getIssueById(issueId) != null && expandedIssueIdsWithSubtasks.indexOf(issueId) >= 0,
					);
					LocalStorage.setExpandedTasks(projectId, cleanedIssueIds);

					markExperienceSuccess();
					const totalSubtaskCount = issues.reduce(
						(total, issue) =>
							total +
							(cleanedIssueIds.indexOf(issue.id) !== -1 ? issue.subtaskMetadata?.total ?? 0 : 0),
						0,
					);
					fireTrackAnalytics(createAnalyticsEvent({}), 'jwmBoardSubtasks loaded', {
						issues: cleanedIssueIds.length,
						subtasks: totalSubtaskCount,
					});
				} else {
					markExperienceFailed(
						'Error loading initial subtasks',
						Error('Error loading initial subtasks'),
					);
				}
			});
		}
	}, [
		issues,
		initialSubtasksLoaded,
		getIssueById,
		batchLoadSubtasks,
		createAnalyticsEvent,
		projectId,
		updateIssue,
		selectedFields,
		startExperience,
		markExperienceSuccess,
		markExperienceFailed,
	]);

	// revert optimistic update when request errors
	useEffect(() => {
		if (errorIssueIds.length > 0) {
			const issuesToRevert = optimisticUpdate.filter(
				(issueId) => errorIssueIds.indexOf(issueId) >= 0,
			);
			issuesToRevert.forEach((key) => {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				updateIssue(+key, { subtasks: undefined } as {
					subtasks: undefined;
				});
			});
			setErrorIssueIds([]);
		}
	}, [optimisticUpdate, errorIssueIds, updateIssue]);
};
